import * as React from "react"
import { ChevronRight } from "lucide-react"
import { Breadcrumb } from "@/components/brand-kit/breadcrumb"
import { brandsBreadcrumbs } from "@/lib/breadcrumbs"
import { SafeImage } from "@/components/ui/safe-image"
import type { IndexBrand } from "./types"
import { isWideWordmark } from "./logo-shape"

/**
 * FRIDGE-1495 (10/17/2026): the pill's logo boxes. A box 20 px tall caps every
 * logo's height; its width caps a wordmark's. In the 88 x 20 box KitchenAid
 * (10:1) painted 8.8 px tall on production phones, and 10.4 in the 104 px box.
 * A wide wordmark (logo-shape.ts, 6.5:1 and wider) gets 124 x 20, 140 x 20
 * from 640 px: KitchenAid 12.4 and 14 px tall, Frigidaire 14 and 15.9.
 * Samsung (6.06:1) keeps 88 x 20, where it paints 14.4 px.
 */
const PILL_LOGO_BOX = "flex h-5 w-[88px] items-center justify-center sm:w-[104px]"
const PILL_WORDMARK_BOX = "flex h-5 w-[124px] items-center justify-center sm:w-[140px]"

interface BrandsHeroProps {
  /** Up to 6 partner brands with logos, shown by their real logos in the count pill. */
  miniStackBrands: IndexBrand[]
  /** Total brand count for the pill label ("174 brands tracked"). */
  totalCount: number
}

/**
 * Brands page editorial hero.
 *
 * Centered block under the global header with a subtle radial gradient
 * behind the title. Structure: topbar (breadcrumb + directory eyebrow) →
 * H1 with orange "Brands" accent → subtitle → brand-logo pill.
 *
 * Trust chips were removed per design feedback — the title + count pill
 * already do the work the chips were doing.
 *
 * Server component — pure render, no state; the logos render through
 * SafeImage, a client component.
 */
export function BrandsHero({ miniStackBrands, totalCount }: BrandsHeroProps) {
  return (
    <section
      className="relative isolate mx-auto max-w-[1100px] pb-10 pt-8 text-center sm:pt-10 lg:pt-12"
      aria-labelledby="brands-hero-h1"
    >
      {/* Subtle ambient gradient behind the title — blue 7% top-left, orange
          6% top-right, fading to transparent before reaching the count pill. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(560px 320px at 22% 18%, rgba(4,105,240,0.07), transparent 60%), radial-gradient(620px 340px at 78% 18%, rgba(255,153,81,0.06), transparent 60%)",
        }}
      />

      {/* Topbar — breadcrumb · vertical divider · directory eyebrow */}
      <div className="mb-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[12.5px]">
        {/* ONE trail (lib/breadcrumbs.ts brandsBreadcrumbs) — app/brands/page.tsx
            emits the identical array as the BreadcrumbList (FRIDGE-269). */}
        <Breadcrumb
          trail={brandsBreadcrumbs()}
          className="font-medium text-gray-500"
          listClassName="flex items-center gap-1.5 list-none m-0 p-0"
          itemClassName="flex items-center gap-1.5"
          linkClassName="hover:text-gray-700"
          currentClassName="font-bold text-gray-800"
          separatorClassName="flex items-center text-gray-300"
          separator={<ChevronRight className="h-2.5 w-2.5" />}
        />
        <span aria-hidden className="hidden h-3.5 w-px bg-gray-300 sm:inline-block" />
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className="h-1.5 w-1.5 rounded-full"
            style={{
              background: "linear-gradient(135deg, #0469f0, #ff9951)",
              boxShadow: "0 0 0 3px rgba(4,105,240,0.12)",
            }}
          />
          <span className="text-[11.5px] font-extrabold uppercase tracking-[0.14em] text-[#0469f0]">
            Fridge.com Brand Directory
          </span>
        </div>
      </div>

      {/* H1 — sized so "Refrigerator & Freezer Brands" stays on one line in the
         max-w-[1100px] container at every breakpoint. Standard Tailwind scale
         (text-4xl..text-6xl = 36/48/60px) — `xl:text-7xl` is left off because
         60px already comfortably fills the 1100px container at xl+. */}
      <h1
        id="brands-hero-h1"
        className="text-4xl font-black leading-[1.02] tracking-tight text-gray-900 sm:text-5xl lg:text-6xl"
      >
        Refrigerator &amp; Freezer{" "}
        <span className="text-[#ff9951]">Brands</span>
      </h1>

      {/* Subtitle */}
      <p className="mx-auto mt-4 max-w-[620px] text-pretty text-[17px] leading-[1.5] text-gray-600">
        Every brand we track in one place — from heritage luxury to everyday value. Compare price
        ranges, product types, and find the right manufacturer for your kitchen.
      </p>

      {/* Brand-logo pill.
          FRIDGE-1495 (owner, 10/05/2026: "stuff like this should always show
          REAL brand logos"): the six partner brands show their real logos.
          FRIDGE-859 had swapped them for initials because each logo sat in a
          32 px circle, where a wordmark (Frigidaire 8.8:1, KitchenAid 10:1)
          shrank to 20 x 2 px. Here each logo gets a wide box instead, 88 x 20 px
          (104 x 20 from 640 px), drawn whole by object-contain: LG 45 x 20.
          A wide wordmark's box is 124 x 20 (140 x 20 from 640 px; see
          PILL_WORDMARK_BOX above). The boxes have a fixed size,
          so the row does not move as the logos load; they sit in the first
          screen, so they load eagerly. Below 640 px the logos wrap two or three
          to a row above the count, and the pill becomes a rounded card. */}
      {miniStackBrands.length > 0 && (
        <div className="mt-6 inline-flex max-w-full flex-wrap items-center justify-center gap-x-5 gap-y-3 rounded-[24px] border border-gray-200 bg-white px-5 py-3 shadow-[0_12px_30px_-16px_rgba(0,0,0,0.12)] sm:rounded-full">
          <ul className="m-0 flex list-none flex-wrap items-center justify-center gap-x-4 gap-y-2 p-0">
            {miniStackBrands.slice(0, 6).map((b) => {
              const wordmark = isWideWordmark(b.logoAspect)
              return (
                <li key={b.slug} className={wordmark ? PILL_WORDMARK_BOX : PILL_LOGO_BOX}>
                  <SafeImage
                    src={b.logo_path!}
                    alt={b.name}
                    width={wordmark ? 140 : 104}
                    height={20}
                    loading="eager"
                    className="object-contain"
                    style={{ width: "100%", height: "100%" }}
                  />
                </li>
              )
            })}
          </ul>
          <div className="text-[13px] font-medium text-gray-600">
            <strong className="font-extrabold text-gray-900">{totalCount.toLocaleString()}</strong>{" "}
            brands tracked ·{" "}
            <span className="font-bold text-[#0469f0]">updated regularly</span>
          </div>
        </div>
      )}
    </section>
  )
}
